// VIBRANIAM-SPACE v2 - nodes/render.js
// Frame-rate nodes. These are not evaluated as functions: the Engine walks
// them, resolves their parameters through the modulation matrix and drives the
// substrates and the render pipeline from the result.

import { FORCE_KINDS, forceByKind, defaultForceParams } from '../forces.js';
import { MESH_KIND_LABELS } from '../substrates/GeometricMeshSubstrate.js';
import { ImageStrobeSubstrate } from '../substrates/ImageStrobeSubstrate.js';

const ACC = {
  force:  '#e8584a',
  field:  '#f0a638',
  mesh:   '#4ad6c0',
  layer:  '#e05a9e',
  camera: '#9c7cf0',
  out:    '#ff7ba9',
};

export const RENDER_NODES = {
  // -------------------------------------------------------------- force
  'r.force': {
    type: 'r.force', title: 'Forza', group: 'Forze', accent: ACC.force,
    hint: 'Una forza della catena. Piu forze sullo stesso campo si sommano.',
    inputs: [],
    outputs: [{ id: 'out', kind: 'force', label: 'forza' }],
    params: { kind: 4, weight: 1, enabled: true, ...defaultForceParams(4) },
    schema: [
      { key: 'kind', label: 'Tipo', type: 'forceKind' },
      { key: 'weight', label: 'Peso', min: -2, max: 2, step: 0.005, mod: true },
      { key: 'enabled', label: 'Attiva', type: 'bool' },
    ],
    // The remaining controls depend on which force this is.
    dynamicSchema(params) {
      return forceByKind(params.kind).params.map((s) => ({
        key: s.key, label: s.label, min: s.min, max: s.max, step: s.step, mod: true,
      }));
    },
    onParamChange(key, value, params) {
      // Switching kind swaps in that kind's own defaults.
      if (key !== 'kind') return null;
      return { kind: Number(value), weight: params.weight, enabled: params.enabled,
               ...defaultForceParams(Number(value)) };
    },
  },

  // -------------------------------------------------------------- field
  'r.field': {
    type: 'r.field', title: 'Campo particellare', group: 'Substrati', accent: ACC.field,
    hint: 'Pool di particelle mosso dalle forze collegate.',
    inputs: [{ id: 'forces', kind: 'force', label: 'forze', multi: true }],
    outputs: [{ id: 'out', kind: 'layer', label: 'layer' }],
    params: {
      capacity: 60000,
      rate: 3000, rateAudio: 12000, burst: 1400,
      lifeMin: 2.0, lifeMax: 6.0,
      speedMin: 0.18, speedMax: 0.85,
      sizeMin: 0.40, sizeMax: 1.80, pointScale: 0.08,
      spawnRadius: 0.90,
      hueBase: 0.56, hueSpread: 0.30, hueBySpeed: 0.22, hueByAge: 0.06,
      saturation: 0.88, brightness: 0.22,
      sizeByBass: 0.9, fadeShape: 1.0,
    },
    schema: [
      { key: 'capacity',    label: 'Capacita',   min: 2000, max: 400000, step: 1000 },
      { key: 'rate',        label: 'Rate base',  min: 0, max: 20000, step: 50, mod: true },
      { key: 'rateAudio',   label: 'Rate audio', min: 0, max: 40000, step: 100, mod: true },
      { key: 'burst',       label: 'Burst onset',min: 0, max: 8000, step: 50, mod: true },
      { key: 'lifeMin',     label: 'Vita min',   min: 0.1, max: 14, step: 0.05, mod: true },
      { key: 'lifeMax',     label: 'Vita max',   min: 0.1, max: 14, step: 0.05, mod: true },
      { key: 'speedMin',    label: 'Vel. min',   min: 0, max: 4, step: 0.01, mod: true },
      { key: 'speedMax',    label: 'Vel. max',   min: 0, max: 4, step: 0.01, mod: true },
      { key: 'sizeMin',     label: 'Dim. min',   min: 0.05, max: 10, step: 0.05, mod: true },
      { key: 'sizeMax',     label: 'Dim. max',   min: 0.05, max: 10, step: 0.05, mod: true },
      { key: 'pointScale',  label: 'Scala punto', min: 0.005, max: 0.2, step: 0.001, mod: true },
      { key: 'spawnRadius', label: 'Raggio nascita', min: 0, max: 4, step: 0.01, mod: true },
      { key: 'hueBase',     label: 'Tinta',      min: 0, max: 1, step: 0.002, mod: true },
      { key: 'hueSpread',   label: 'Ampiezza tinta', min: 0, max: 1, step: 0.002, mod: true },
      { key: 'hueBySpeed',  label: 'Tinta / velocita', min: -1, max: 1, step: 0.005, mod: true },
      { key: 'hueByAge',    label: 'Tinta / eta', min: -1, max: 1, step: 0.005, mod: true },
      { key: 'saturation',  label: 'Saturazione', min: 0, max: 1, step: 0.005, mod: true },
      { key: 'brightness',  label: 'Luminosita', min: 0, max: 3, step: 0.005, mod: true },
      { key: 'sizeByBass',  label: 'Dim. / bassi', min: 0, max: 4, step: 0.01, mod: true },
      { key: 'fadeShape',   label: 'Forma fade', min: 0.2, max: 4, step: 0.02, mod: true },
    ],
  },

  // -------------------------------------------------------------- mesh
  // Second substrate, fully independent of the particle field: it feeds a layer
  // through the same 'layer' port, so a patch can use one, the other, or both
  // on the same layer.
  'r.mesh': {
    type: 'r.mesh', title: 'Geometria 3D', group: 'Substrati', accent: ACC.mesh,
    hint: 'Struttura geometrica procedurale deformata dallo spettro audio.',
    inputs: [],
    outputs: [{ id: 'out', kind: 'layer', label: 'layer' }],
    params: {
      kind: 'Sfera', resolution: 48, wireframe: true,
      displace: 0.60, wave: 0.05, waveSpeed: 1.2,
      twist: 0.20, jitter: 0.15, pulse: 0.25, spin: 0.15,
      hueBase: 0.55, hueSpread: 0.25, hueByBand: 0.30,
      saturation: 0.85, brightness: 0.90, opacity: 1.0, gain: 0.80,
    },
    schema: [
      { key: 'kind',       label: 'Forma',       type: 'select', options: MESH_KIND_LABELS },
      { key: 'resolution', label: 'Risoluzione', min: 4, max: 160, step: 1 },
      { key: 'wireframe',  label: 'Wireframe',   type: 'bool' },
      { key: 'displace',   label: 'Spettro / rilievo', min: 0, max: 4, step: 0.005, mod: true },
      { key: 'wave',       label: 'Onda',        min: 0, max: 1.5, step: 0.005, mod: true },
      { key: 'waveSpeed',  label: 'Vel. onda',   min: -8, max: 8, step: 0.02, mod: true },
      { key: 'twist',      label: 'Torsione',    min: -3, max: 3, step: 0.005, mod: true },
      { key: 'jitter',     label: 'Tremolio / alti', min: 0, max: 2, step: 0.005, mod: true },
      { key: 'pulse',      label: 'Pulsazione / bassi', min: 0, max: 2, step: 0.005, mod: true },
      { key: 'spin',       label: 'Rotazione',   min: -3, max: 3, step: 0.005, mod: true },
      { key: 'hueBase',    label: 'Tinta',       min: 0, max: 1, step: 0.002, mod: true },
      { key: 'hueSpread',  label: 'Tinta / energia', min: -1, max: 1, step: 0.005, mod: true },
      { key: 'hueByBand',  label: 'Tinta / banda', min: -1, max: 1, step: 0.005, mod: true },
      { key: 'saturation', label: 'Saturazione', min: 0, max: 1, step: 0.005, mod: true },
      { key: 'brightness', label: 'Luminosita',  min: 0, max: 3, step: 0.005, mod: true },
      { key: 'opacity',    label: 'Opacita',     min: 0, max: 2, step: 0.005, mod: true },
      { key: 'gain',       label: 'Guadagno',    min: 0, max: 3, step: 0.005, mod: true },
    ],
  },

  // -------------------------------------------------------------- layer
  'r.layer': {
    type: 'r.layer', title: 'Layer', group: 'Render', accent: ACC.layer,
    hint: 'Buffer a virgola mobile con feedback e bloom. Qui nasce la scia.',
    inputs: [{ id: 'in', kind: 'layer', label: 'substrati', multi: true }],
    outputs: [{ id: 'out', kind: 'video', label: 'video' }],
    params: {
      opacity: 1,
      blend: 'add',
      feedback: 0.80,
      feedbackZoom: 1.004,
      feedbackRotate: 0.0012,
      feedbackWarp: 0.10,
      bloom: 0.80,
      bloomThreshold: 0.45,
      bloomRadius: 0.45,
    },
    schema: [
      { key: 'opacity',        label: 'Opacita',        min: 0, max: 2, step: 0.005, mod: true },
      { key: 'blend',          label: 'Fusione',        type: 'select', options: ['add', 'screen', 'alpha'] },
      { key: 'feedback',       label: 'Feedback',       min: 0, max: 0.995, step: 0.001, mod: true },
      { key: 'feedbackZoom',   label: 'Zoom scia',      min: 0.95, max: 1.06, step: 0.0005, mod: true },
      { key: 'feedbackRotate', label: 'Rotaz. scia',    min: -0.02, max: 0.02, step: 0.0002, mod: true },
      { key: 'feedbackWarp',   label: 'Deformaz. scia', min: 0, max: 1.5, step: 0.005, mod: true },
      { key: 'bloom',          label: 'Bloom',          min: 0, max: 3, step: 0.01, mod: true },
      { key: 'bloomThreshold', label: 'Soglia bloom',   min: 0, max: 2, step: 0.005, mod: true },
      { key: 'bloomRadius',    label: 'Raggio bloom',   min: 0.05, max: 1.5, step: 0.005, mod: true },
    ],
  },

  // -------------------------------------------------------------- camera
  'r.camera': {
    type: 'r.camera', title: 'Camera', group: 'Render', accent: ACC.camera,
    hint: 'Distanza e orbita. Modulare la distanza sui bassi e uno dei trucchi migliori.',
    inputs: [],
    outputs: [{ id: 'out', kind: 'camera', label: 'camera' }],
    params: { distance: 7.5, fov: 62, orbit: true, orbitSpeed: 0.08, tilt: 0.18, roll: 0 },
    schema: [
      { key: 'distance',   label: 'Distanza',  min: 0.5, max: 30, step: 0.02, mod: true },
      { key: 'fov',        label: 'FOV',       min: 15, max: 110, step: 0.5, mod: true },
      { key: 'orbit',      label: 'Orbita',    type: 'bool' },
      { key: 'orbitSpeed', label: 'Vel. orbita', min: -1.5, max: 1.5, step: 0.002, mod: true },
      { key: 'tilt',       label: 'Inclinaz.', min: -1, max: 1, step: 0.005, mod: true },
      { key: 'roll',       label: 'Rollio',    min: -3.15, max: 3.15, step: 0.005, mod: true },
    ],
  },

  // -------------------------------------------------------------- image strobe
  'r.imageStrobe': {
    type: 'r.imageStrobe', title: 'Image Strobe', group: 'Render', accent: '#ff7ba9',
    hint: 'Sequenza di immagini che riempiono il viewport senza deformazione.',
    inputs: [],
    outputs: [{ id: 'out', kind: 'layer', label: 'layer' }],
    params: {
      source: 'user', // 'user' (loaded via picker) or 'library' (fotostrobo manifest)
      images: [], // array of { url: string, weight: number }
      mode: 'sequential', // 'sequential' or 'random'
      speed: 1.0, // Hz, hard-clamped to 3
      confirmed: false, // whether the user has confirmed the fotosensitivity warning
    },
    schema: [
      { key: 'source', label: 'Fonte', type: 'select', options: ['user', 'library'] },
      { key: 'images', label: 'Immagini', type: 'custom', hint: 'Carica immagini tramite l\'interfaccia' },
      { key: 'mode', label: 'Ordine', type: 'select', options: ['sequential', 'random'] },
      { key: 'speed', label: 'Velocità (Hz)', min: 0.1, max: 3, step: 0.1, mod: true },
      { key: 'confirmed', label: 'Conferma avviso', type: 'bool' },
    ],
  },

  // -------------------------------------------------------------- mixer / switcher
  'r.mixer': {
    type: 'r.mixer', title: 'Mixer / Switcher', group: 'Render', accent: '#ffc14d',
    hint: 'Tagli secchi tra immagini, sfondi generati e l\'animazione particellare.',
    inputs: [{ id: 'strobe', kind: 'layer', label: 'immagini', multi: true }],
    outputs: [{ id: 'out', kind: 'layer', label: 'layer' }],
    params: {
      mode: 'sequential', // 'sequential' or 'random'
      speed: 1.0, // Hz, hard-clamped to 3
      background: 'white', // 'white' | 'grid' | 'circle'
      includeStrobe: true,
      includeBackground: true,
      includeAnimation: true,
    },
    schema: [
      { key: 'mode', label: 'Ordine', type: 'select', options: ['sequential', 'random'] },
      { key: 'speed', label: 'Velocità (Hz)', min: 0.1, max: 3, step: 0.1, mod: true },
      { key: 'background', label: 'Sfondo', type: 'select', options: ['white', 'grid', 'circle'] },
      { key: 'includeStrobe', label: 'Includi immagini', type: 'bool' },
      { key: 'includeBackground', label: 'Includi sfondi', type: 'bool' },
      { key: 'includeAnimation', label: 'Includi animazione', type: 'bool' },
    ],
  },

  // -------------------------------------------------------------- output
  'r.output': {
    type: 'r.output', title: 'Uscita', group: 'Render', accent: ACC.out,
    hint: 'Composizione finale e impostazioni di export.',
    inputs: [
      { id: 'in',     kind: 'video',  label: 'video', multi: true },
      { id: 'camera', kind: 'camera', label: 'camera' },
    ],
    outputs: [],
    params: {
      width: 1920, height: 1080, fps: 60, duration: 12, crf: 16,
      exposure: 1.0, background: '#03040a', vignette: 0.42, grain: 0.012,
      stereo: 'off', stereoStrength: 0.7, format: 'mp4',
    },
    schema: [
      { key: 'width',      label: 'Larghezza', min: 256, max: 3840, step: 2 },
      { key: 'height',     label: 'Altezza',   min: 256, max: 2160, step: 2 },
      { key: 'fps',        label: 'FPS',       type: 'select', options: [24, 25, 30, 50, 60] },
      { key: 'duration',   label: 'Durata s',  min: 1, max: 600, step: 1 },
      { key: 'crf',        label: 'CRF',       min: 8, max: 28, step: 1 },
      { key: 'format',     label: 'Formato',   type: 'select', options: ['mp4', 'webm'] },
      { key: 'exposure',   label: 'Esposizione', min: 0, max: 4, step: 0.01, mod: true },
      { key: 'background', label: 'Sfondo',    type: 'color' },
      { key: 'vignette',   label: 'Vignettatura', min: 0, max: 1.5, step: 0.005, mod: true },
      { key: 'grain',      label: 'Grana',     min: 0, max: 0.12, step: 0.001, mod: true },
      { key: 'stereo', label: 'Stereo', type: 'select', options: ['off', 'anaglyph', 'sbs', 'wiggle'] },
      { key: 'stereoStrength', label: 'Intensità', min: 0, max: 1, step: 0.01, mod: true },
    ],
  },
};

export const RENDER_TYPES = Object.keys(RENDER_NODES);
export const isRenderNode = (type) => Object.prototype.hasOwnProperty.call(RENDER_NODES, type);
export { FORCE_KINDS };
